import { faDollarSign } from "@fortawesome/free-solid-svg-icons/faDollarSign";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { Tooltip } from "../.././../utils/local-antd";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import noop from "lodash/noop";
import React, { useCallback, useEffect, useState } from "react";
import {
  Controller,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { UpdateKanbanCardDto } from "../../../model/kanban/dto/UpdateKanbanCardDto";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { comparePayloads } from "../../../utils/ComparePayloads";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { getTranslatedKanbanColumnName } from "../KanbanUtils";
import { useLocation } from "react-router-dom";
import { appSliceSelectors } from "../../../redux/app/app.selector";
import { AtiraDatePicker } from "../../../components/AtiraDatePicker";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { useDrawersContext } from "../../tasks/drawers-context";
import fetchAfterKanbanCardAction from "../functions/fetchAfterKanbanCardAction";
import { GetTasksFilterDto } from "../../../model/task/dto/GetTasksFilterDto";
import { taskSliceSelectors } from "../../../redux/task/task.selector";
import { TestIDs } from "../../../tests/types/testIds";

const StyledInput = styled(Input)`
  height: 2.3rem;
`;

const CustomFieldButton = styled(Button)`
  margin: 0;
  height: auto;
  width: 2.4rem;
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.main};
`;

export const KanbanCardUpdateDrawer: React.FC = () => {
  const [kanbanCardUpdateLoading, setKanbanCardUpdateLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const {
    kanbanCard,
    setKanbanCard,
    kanbanCardUpdateVisible,
    setKanbanCardUpdateVisible,
  } = useDrawersContext();
  const { control, handleSubmit, getValues, reset, watch, setError } =
    useForm<UpdateKanbanCardDto>();
  const { getValues: contextGetValues } = useFormContext<GetTasksFilterDto>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "customFields",
  });

  const userKanban = useAppSelector(
    kanbanSliceSelectors.selectUserCurrentKanban,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const searchOptions = useAppSelector(appSliceSelectors.selectSearchOptions);
  const tasksPage = useAppSelector(taskSliceSelectors.selectTasksPage);
  const tasksPageSize = useAppSelector(taskSliceSelectors.selectTasksPageSize);

  const [title, columnId] = watch(["title", "columnId"]);
  const kanbanColumn = userKanban?.columns?.find(
    (c) => c._id === kanbanCard?.parentId,
  );

  const setInitialvalues = useCallback(() => {
    reset({
      title: kanbanCard?.title || "",
      kanbanId: userKanban?._id || "",
      columnId: kanbanCard?.parentId || "",
      cardId: kanbanCard?._id || "",
      note: kanbanCard?.note || "",
      closingDate: kanbanCard?.closingDate
        ? new Date(kanbanCard?.closingDate)
        : undefined,
      amount: kanbanCard?.amount || "",
      customFields: kanbanCard?.customFields || [],
    });
  }, [kanbanCard, reset, userKanban?._id]);

  const localOnClose = () => {
    setKanbanCardUpdateVisible(false);
    setInitialvalues();
  };

  const onUpdateKanbanCard = async () => {
    try {
      if (!kanbanCard) {
        return;
      }

      setKanbanCardUpdateLoading(true);

      const dto = getValues();

      const { kanbanId, columnId, cardId, ...modifiableDto } = dto;

      const hasCustomFieldsChanged = !isEqual(
        kanbanCard.customFields,
        dto.customFields,
      );

      const updatedDto = comparePayloads(
        kanbanCard,
        modifiableDto,
      ) as UpdateKanbanCardDto;

      if (updatedDto.closingDate) {
        updatedDto.closingDate = new Date(updatedDto.closingDate);
      }

      const payload = {
        ...updatedDto,
        ...(hasCustomFieldsChanged
          ? { customFields: [...dto.customFields!] }
          : {}),
      };

      if (!isEmpty(payload)) {
        await dispatch(
          kanbanActions.updateKanbanCard({
            ...payload,
            userId,
            kanbanId: userKanban?._id!,
            columnId: columnId,
            cardId,
          }),
        ).unwrap();
      }

      await fetchAfterKanbanCardAction({
        dispatch,
        pathname: location.pathname,
        userId,
        searchKeyword: searchOptions?.keyword,
        setKanbanCard,
        kanbanCardId: cardId,
        contextGetValues: contextGetValues,
        tasksPage,
        tasksPageSize,
      });

      localOnClose();
      AtiraToast.success(t("deals.drawer.update.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setKanbanCardUpdateLoading(false);
    }
  };

  useEffect(() => {
    setInitialvalues();
  }, [setInitialvalues]);

  if (!kanbanCard) {
    return null;
  }

  return (
    <Drawer
      destroyOnHidden
      title={t("deals.drawer.edit.title")}
      open={kanbanCardUpdateVisible}
      onClose={localOnClose}
      size="large"
      maskClosable={false}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onUpdateKanbanCard)}
          onCancel={localOnClose}
          config={{
            loading: kanbanCardUpdateLoading,
            editMode: true,
            disabled: !title || !columnId,
          }}
          submitTestId={TestIDs.kanbans.card.edit.drawer.button}
        />
      }
    >
      <Flex flexDirection="column" gap="m">
        <Controller
          name="title"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledInput
              value={value}
              onChange={onChange}
              title={t("deals.drawer.input.deal_name")}
              valid={!error}
              errorMessage={error?.message}
              required
              dataTestId={TestIDs.kanbans.card.edit.drawer.name}
            />
          )}
        />

        <AtiraSelect
          onChange={noop}
          showSearch
          filterOption={false}
          value={
            kanbanCard.entry?.fields?.name ||
            kanbanCard.entry?.fields?.email ||
            kanbanCard.entryId
          }
          title={t("deals.drawer.input.contact_name")}
          disabled
          className={TestIDs.kanbans.card.edit.drawer.selectContact.input}
        />

        <Controller
          name="columnId"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const isCardInFirstThreeColumns =
              userKanban?.columns &&
              userKanban.columns.findIndex(({ _id }) => _id === value) < 3;

            const isCardInLastThreeColumns =
              userKanban?.columns &&
              userKanban.columns.findIndex(({ _id }) => _id === value) >=
                userKanban.columns.length - 3;

            const filteredColumns = userKanban?.columns?.filter(
              (column, index) =>
                isCardInFirstThreeColumns ? index < 3 : index >= 3,
            );

            return (
              <AtiraSelect
                defaultValue={{
                  label: getTranslatedKanbanColumnName(
                    kanbanColumn?.name,
                    userKanban?.template,
                  ),
                  value: kanbanColumn?._id,
                }}
                value={value}
                options={filteredColumns?.map(({ _id, name }) => ({
                  label: getTranslatedKanbanColumnName(
                    name,
                    userKanban?.template,
                  ),
                  value: _id,
                  "data-testid":
                    TestIDs.kanbans.card.edit.drawer.stage.option(_id),
                }))}
                onChange={onChange}
                title={t("common.stage")}
                disabled={isCardInLastThreeColumns}
                valid={!error}
                errorMessage={error?.message}
                className={TestIDs.kanbans.card.edit.drawer.stage.open}
              />
            );
          }}
        />

        <Flex position="relative">
          <Controller
            name="amount"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <StyledInput
                value={value}
                onChange={onChange}
                title={t("common.amount")}
                icon={faDollarSign}
                valid={!error}
                errorMessage={error?.message}
                dataTestId={TestIDs.kanbans.card.edit.drawer.amount}
              />
            )}
          />
        </Flex>

        <Controller
          name="closingDate"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <AtiraDatePicker
              title={t("deals.drawer.input.closing_date")}
              onChange={onChange}
              value={value || null}
              valid={!error}
              showTime
              fromToday
              dataTestId={TestIDs.kanbans.card.edit.drawer.closingDate}
            />
          )}
        />

        <Controller
          name="note"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledInput
              value={value}
              onChange={onChange}
              title={t("common.note")}
              type="text"
              valid={!error}
              errorMessage={error?.message}
              dataTestId={TestIDs.kanbans.card.edit.drawer.note}
            />
          )}
        />

        <Flex width={"100%"} flexDirection="column" gap="s">
          <Flex marginBottom="s" marginTop="s" flexDirection="column">
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontWeight={600}>{t("common.custom_fields")}</Text>

              <Tooltip title={t("common.tooltip.add_new_field")}>
                <CustomFieldButton
                  icon={faPlus}
                  iconWidth="2x"
                  disabled={fields.length >= 3}
                  onClick={() => append({ label: "", value: "" })}
                  dataTestId={
                    TestIDs.kanbans.card.edit.drawer.customFields.button
                  }
                />
              </Tooltip>
            </Flex>

            <Text wordBreak="break-word" color="gray" fontSize="s">
              {t("common.custom_fields.note")}
            </Text>
          </Flex>

          <Flex gap="m" flexDirection="column" width={"100%"}>
            {fields.map((field, index) => (
              <Flex flexDirection="column" gap="s" key={field.id}>
                <Controller
                  control={control}
                  name={`customFields.${index}.label`}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      value={value}
                      onChange={onChange}
                      border="0"
                      placeholder={t("common.input.edit_label")}
                      maxLength={Lengths.NAME}
                      padding="initial 0"
                      valid={!error}
                      errorMessage={error?.message}
                      required
                      dataTestId={TestIDs.kanbans.card.edit.drawer.customFields.field.label(
                        index.toString(),
                      )}
                    />
                  )}
                />

                <Flex justifyContent="space-between" gap="s">
                  <Controller
                    control={control}
                    name={`customFields.${index}.value`}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <StyledInput
                        value={value}
                        onChange={onChange}
                        placeholder={t("common.input.edit_value")}
                        maxLength={Lengths.DESCRIPTION}
                        valid={!error}
                        errorMessage={error?.message}
                        required
                        dataTestId={TestIDs.kanbans.card.edit.drawer.customFields.field.value(
                          index.toString(),
                        )}
                      />
                    )}
                  />

                  <Tooltip title={t("common.tooltip.remove_this_field")}>
                    <CustomFieldButton
                      icon={faTrash}
                      iconWidth="2x"
                      onClick={() => remove(index)}
                    />
                  </Tooltip>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Drawer>
  );
};
